import React, { useEffect, useMemo, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  addDays,
  addMonths,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfWeek,
} from "date-fns";
import { makeStyles } from "tss-react/mui";

import AppointmentsMonthView from "./AppointmentsMonthView";
import AppointmentsTimeGrid from "./AppointmentsTimeGrid";
import { CalendarAppointment, DraftSlot } from "./geometry";

export type CalendarMode = "day" | "week" | "month";

type Props = {
  appointments: CalendarAppointment[];
  onView: (id: string) => void;
  onCreate: (date: Date, startMin: number, endMin: number) => void;
  onReschedule: (
    id: string,
    date: Date,
    startMin: number,
    endMin: number
  ) => void;
  // Placeholder for the appointment being encoded in the create drawer.
  draftSlot?: DraftSlot | null;
  onDraftMove?: (date: Date, startMin: number, endMin: number) => void;
};

const AppointmentsCalendar: React.FC<Props> = ({
  appointments,
  onView,
  onCreate,
  onReschedule,
  draftSlot,
  onDraftMove,
}) => {
  const { classes } = useStyles();
  const [mode, setMode] = useState<CalendarMode>("week");
  const [focusedDate, setFocusedDate] = useState(() => new Date());

  // Keep the placeholder in view: when its date changes (edited in the form
  // or dragged to another day), navigate to the period containing it.
  const draftDateKey = draftSlot ? format(draftSlot.date, "yyyy-MM-dd") : null;
  useEffect(() => {
    if (!draftSlot) return;
    const target = draftSlot.date;
    setFocusedDate((current) => {
      const visible =
        mode === "day"
          ? isSameDay(current, target)
          : mode === "week"
          ? isWithinInterval(target, {
              start: startOfWeek(current),
              end: endOfWeek(current),
            })
          : isSameMonth(current, target);
      return visible ? current : target;
    });
  }, [draftDateKey, mode]);

  const weekDates = useMemo(() => {
    const start = startOfWeek(focusedDate);
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [focusedDate]);

  const shift = (direction: 1 | -1) => {
    setFocusedDate((current) => {
      if (mode === "day") return addDays(current, direction);
      if (mode === "week") return addDays(current, direction * 7);
      return addMonths(current, direction);
    });
  };

  const periodLabel = useMemo(() => {
    if (mode === "day") return format(focusedDate, "EEEE, MMM d, yyyy");
    if (mode === "month") return format(focusedDate, "MMMM yyyy");
    const start = startOfWeek(focusedDate);
    const end = endOfWeek(focusedDate);
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }, [mode, focusedDate]);

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Box className={classes.nav}>
          <Button
            variant="outlined"
            size="small"
            className={classes.today}
            onClick={() => setFocusedDate(new Date())}
          >
            Today
          </Button>
          <IconButton size="small" onClick={() => shift(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton size="small" onClick={() => shift(1)}>
            <ChevronRightIcon />
          </IconButton>
          <Typography className={classes.period}>{periodLabel}</Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => next && setMode(next)}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "month" ? (
        <AppointmentsMonthView
          focusedDate={focusedDate}
          appointments={appointments}
          onView={onView}
          onSelectDate={(date) => {
            setFocusedDate(date);
            setMode("day");
          }}
          draftDate={draftSlot?.date ?? null}
        />
      ) : (
        <AppointmentsTimeGrid
          dates={mode === "day" ? [focusedDate] : weekDates}
          appointments={appointments}
          onView={onView}
          onCreate={onCreate}
          onReschedule={onReschedule}
          draftSlot={draftSlot}
          onDraftMove={onDraftMove}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  today: {
    marginRight: 8,
    fontWeight: 700,
    borderColor: "#D4DBE5",
    color: "#3D6DF0",
  },
  period: {
    marginLeft: 8,
    fontSize: 18,
    fontWeight: 700,
    color: "#363E45",
  },
}));

export default AppointmentsCalendar;
